/*
 * @Description: 数据转换
 * @Date: 2024-12-16 09:56:12
 */
import { Tree } from '@cTypes/common';
import { date } from './formatter';

/**
 * @description: 递归数组把目标字段放到一维数组中
 * @param {Array} list 数组
 * @param {String} listKey 子集字段名
 * @param {String} targetKey 目标字段名
 * @param {Boolean} all 是否需要把父级的目标字段也加到一维数组中
 * @return {Array} 返回一维数组
 */
export const fieldsToArr: (
  list: Array<Record<string, any>>,
  listKey: string,
  targetKey: string,
  all?: boolean
) => Array<any> = (list, listKey, targetKey, all = false) => {
  const data = list.reduce((pre, cur) => {
    if (cur[listKey]?.length) {
      if (all && cur[targetKey]) {
        pre.push(cur[targetKey]);
      }
      const values = fieldsToArr(cur[listKey], listKey, targetKey);
      [].push.apply(pre, values);
    } else {
      pre.push(cur[targetKey]);
    }
    return pre;
  }, []) as Array<any>;
  return data;
};
export const fieldsToMapping: (
  list: Array<Record<string, any>>,
  listKey: string,
  targetKey: string,
  all?: boolean
) => Record<string, any> = (list, listKey, targetKey, all = false) => {
  const data = list.reduce((pre, cur) => {
    if (cur[listKey]?.length) {
      if (all && cur[targetKey]) {
        pre[cur[targetKey]] = cur;
      }
      const mapping = fieldsToMapping(cur[listKey], listKey, targetKey);
      pre = { ...pre, ...mapping };
    } else {
      pre[cur[targetKey]] = cur;
    }
    return pre;
  }, {}) as Record<string, any>;
  return data;
};

/**
 * @description: cascader 结构数据，根据值获取对应的label
 * @returns {*}
 */
export function getLabelsDirectly(
  options,
  arr,
  props = {
    value: 'value',
    label: 'label',
    children: 'children',
    excludeId: null // 去掉id对应的中文
  }
) {
  if (!arr || !arr.length) return '';
  const valueKey = props?.value || 'id';
  const labelKey = props?.label || 'name';
  const childrenKey = props?.children || 'children';
  const excludeId = props?.excludeId || null;

  let currentOptions = options;
  const labels = [];

  for (const value of arr) {
    const found = currentOptions.find(option => option[valueKey] === value);
    if (found) {
      if (found[valueKey] !== excludeId) labels.push(found[labelKey]);
      currentOptions = found[childrenKey] || [];
    } else {
      return '';
    }
  }

  if (labels && labels?.length) return labels.join('_');
  return '';
}

export function getLabels(
  options,
  contentValue,
  type: 'select' | 'cascader',
  props?,
  multiple?
) {
  if (type === 'select') {
    if (!contentValue) return '';
    if (multiple) {
      return options.reduce((acc, item) => {
        if (contentValue.includes(item.value)) {
          acc += (acc ? '、' : '') + item.label;
        }
        return acc;
      }, '');
    }
    return options?.find(item => item.value === contentValue)?.label || '';
  }

  if (!Array.isArray(contentValue) || !contentValue.length) {
    return '';
  }

  const isNestedArray = contentValue.some(item => Array.isArray(item));

  if (isNestedArray) {
    const labelsArr = [];
    for (const value of contentValue) {
      labelsArr.push(
        getLabelsDirectly(options, value.filter(Boolean), props || {})
      );
    }
    return labelsArr.filter(Boolean).join('、');
  } else {
    return getLabelsDirectly(
      options,
      contentValue.filter(Boolean),
      props || {}
    );
  }
}
export const yuan2fen = (value: number): number =>
  Math.round((Number(value) || 0) * 100); // 提交时转成分  注意0.007 * 100 失准 .0000000001 .9999999996
/**
 * @description: 分转元
 * @param {number} value
 * @param {boolean} round 是否四舍五入
 * @return {*}
 */
export const fen2yuan = (value: number, toFixed?: boolean): number => {
  const money = (Number(value) || 0) / 100;
  if (toFixed) return +money.toFixed(2);
  return money;
};

/**
 * @description: 获取时间区间
 * @param {*} baseStart
 * @param {*} baseEnd
 * @param {*} type
 * @returns {*}
 */
export const getDateRange = (
  baseStart,
  baseEnd,
  format: 'date' | 'month' | 'datetime' = 'date'
) => {
  if (!baseStart && !baseEnd) return '-';

  const startDt = date(baseStart, format);
  const endDt = date(baseEnd, format);

  if (startDt === '-' && endDt === '-') return '-';
  if (baseStart === baseEnd || baseEnd === '-') return startDt;
  if (baseStart === '-') return endDt;

  return `${startDt} - ${endDt}`.replaceAll('- -', '');
};

/**
 * @description: 根据id值或某个属性的值从树结构中查询数据
 * @param {*}
 * @returns {*}
 */
export const findNodeById = ({
  tree,
  id,
  idKey = 'id',
  childrenKey = 'children'
}) => {
  if (!tree || !tree.length) return null;

  for (let i = 0; i < tree.length; i++) {
    if (tree[i][idKey] === id) return tree[i];

    if (tree[i][childrenKey] && tree[i][childrenKey].length) {
      const found = findNodeById({
        tree: tree[i][childrenKey],
        id,
        idKey,
        childrenKey
      });
      if (found) return found;
    }
  }

  return null;
};
/**
 * @description: 树形结构数据转扁平数组
 * @param {object} tree
 * @param {string|number} targetId 查询所有item.value为targetId的数据
 * @param {string|number} parentId 查询所有item.parentId为parentId的数据
 * @return {Array} 一维数组
 */
export const tree2Array = (
  tree: Tree,
  options?: {
    targetId?: string | number;
    parentId?: string | number;
    childrenKey?: 'children';
    valueKey?: 'value';
  }
) => {
  const {
    childrenKey = 'children',
    valueKey = 'value',
    targetId,
    parentId
  } = options || {};
  const result: any[] = [];
  const dfs = (nodes: Tree) => {
    for (const node of nodes) {
      const isMatchTargetId =
        targetId === undefined || node[valueKey] === targetId;
      const isMatchParentId =
        parentId === undefined || node.parentId === parentId;

      if (isMatchTargetId && isMatchParentId) {
        result.push(node);
      }
      if (node[childrenKey] && node[childrenKey].length > 0) {
        dfs(node[childrenKey]);
      }
    }
  };
  dfs(tree);
  return result;
};
